import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { XMarkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import spektrixAuth from '@lib/spektrix/auth'

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const result = await spektrixAuth.login(email, password)

      if (result.success) {
        setEmail('')
        setPassword('')
        onClose()
        if (onLoginSuccess) {
          onLoginSuccess(result.user)
        }
      } else {
        setError(result.error)
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setEmail('')
    setPassword('')
    setError('')
    setShowPassword(false)
    onClose()
  }

  if (!isOpen) return null

  const modalContent = (
    <div
      className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50'
      style={{ zIndex: 9999 }}
    >
      <div className='absolute inset-0' onClick={handleClose} />

      <div
        className='relative w-full max-w-md p-6 bg-white dark:bg-gray-800 shadow-xl rounded-lg mx-4'
        style={{ zIndex: 10000 }}
      >
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
            Sign In
          </h3>
          <button
            onClick={handleClose}
            className='text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
          >
            <XMarkIcon className='w-6 h-6' />
          </button>
        </div>

        <form onSubmit={handleSubmit} className='space-y-4'>
          {error && (
            <div className='p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-400 rounded-md'>
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor='email'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              Email Address
            </label>
            <input
              type='email'
              id='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              required
              disabled={isLoading}
            />
          </div>

          <div>
            <label
              htmlFor='password'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
            >
              Password
            </label>
            <div className='relative'>
              <input
                type={showPassword ? 'text' : 'password'}
                id='password'
                value={password}
                onChange={e => setPassword(e.target.value)}
                className='w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                required
                disabled={isLoading}
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeSlashIcon className='w-5 h-5' />
                ) : (
                  <EyeIcon className='w-5 h-5' />
                )}
              </button>
            </div>
          </div>

          <div className='flex justify-end space-x-3 pt-4'>
            <button
              type='button'
              onClick={handleClose}
              className='px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors'
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50'
              disabled={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </div>
        </form>

        <div className='flex flex-col items-center space-y-2 border-t border-gray-200 dark:border-gray-700 mt-4 pt-4'>
          <p className='text-sm text-gray-600 dark:text-gray-400 text-center'>
            Forgotten your password?{' '}
            <a
              href='/reset-password'
              className='text-blue-600 dark:text-blue-400 hover:underline'
              onClick={handleClose}
            >
              Reset it here
            </a>
          </p>
          <p className='text-sm text-gray-600 dark:text-gray-400 text-center'>
            Don't have an account?{' '}
            <a
              href='/register'
              className='text-blue-600 dark:text-blue-400 hover:underline'
              onClick={handleClose}
            >
              Create one here
            </a>
          </p>
        </div>
      </div>
    </div>
  )

  // Use React Portal to render modal at document body level
  return ReactDOM.createPortal(modalContent, document.body)
}

export default LoginModal
